// Geometry + scale helpers for the takeoff workspace.
// Points are stored in PDF user-space units (1pt = 1/72 inch on paper).

export const PT_TO_M = 0.0254 / 72; // metres of paper per PDF point

export const SCALE_PRESETS = [50, 100, 200, 250, 500, 750, 1000, 1250, 2000];

export const mPerUnitForRatio = (ratio) => ratio * PT_TO_M;

export const MEASURE_TYPES = {
  length: { label: "Length", unit: "m", closed: false, min: 2 },
  area: { label: "Area", unit: "m²", closed: true, min: 3 },
  count: { label: "Count", unit: "ea", closed: false, min: 1 },
  volume: { label: "Volume", unit: "m³", closed: true, min: 3 },
  trench: { label: "Trench", unit: "m³", closed: false, min: 2 },
  pipe: { label: "Pipe Run", unit: "m", closed: false, min: 2 },
  kerb: { label: "Kerb & Channel", unit: "m", closed: false, min: 2 },
  pavement: { label: "Pavement", unit: "m²", closed: true, min: 3 },
  topsoil: { label: "Topsoil Strip", unit: "m³", closed: true, min: 3 },
  cutfill: { label: "Cut / Fill", unit: "m³", closed: true, min: 3 },
  structure: { label: "Structure", unit: "ea", closed: false, min: 1 },
};

export const STRUCTURE_TEMPLATES = [
  "Manhole 1050Ø", "Manhole 1200Ø", "Catchpit", "Sump", "Valve Chamber",
  "Headwall", "Custom structure",
];

export function polyLen(pts) {
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  return d;
}

export function polyArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(a) / 2;
}

// Real-world quantity for a measurement given metres-per-unit scale.
export function computeQty(m, mPerUnit) {
  const pts = m.points || [];
  if (["count", "structure"].includes(m.type)) return pts.length;
  if (!mPerUnit) return 0;
  if (["length", "pipe", "kerb"].includes(m.type)) return polyLen(pts) * mPerUnit;
  if (["area", "pavement"].includes(m.type)) return polyArea(pts) * mPerUnit * mPerUnit;
  if (["volume", "topsoil", "cutfill"].includes(m.type)) return polyArea(pts) * mPerUnit * mPerUnit * (m.depth || 0);
  if (m.type === "trench") return polyLen(pts) * mPerUnit * (m.width || 0) * (m.depth || 0);
  return 0;
}

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Civil smart-tool derived quantities (bedding, backfill, spoil, pavement
// layers…). Keeps existing rates on re-compute by key.
export function computeDerived(m, mPerUnit) {
  const meta = m.meta || {};
  const prev = Object.fromEntries((m.derived || []).map((d) => [d.key, d]));
  const keep = (key, name, qty, unit) => ({ key, name, qty: r2(qty), unit, rate: prev[key]?.rate ?? null });
  if (m.type === "trench" && mPerUnit) {
    const L = polyLen(m.points || []) * mPerUnit;
    const W = m.width || 0, D = m.depth || 0;
    const exc = L * W * D;
    const bed = L * W * ((meta.bedding_mm ?? 150) / 1000);
    const disp = meta.pipe_displacement && meta.pipe_dn_mm
      ? L * Math.PI * Math.pow(meta.pipe_dn_mm / 2000, 2) : 0;
    const backfill = Math.max(exc - bed - disp, 0);
    const spoil = exc * (meta.bulking ?? 1.2);
    return [
      keep("bedding", `Bedding ${meta.bedding_mm ?? 150}mm`, bed, "m³"),
      keep("backfill", `Backfill${meta.backfill ? " " + meta.backfill : ""}`, backfill, "m³"),
      keep("spoil", `Spoil cartage (loose ×${meta.bulking ?? 1.2})`, spoil, "m³"),
    ];
  }
  if (m.type === "pavement" && mPerUnit) {
    const area = polyArea(m.points || []) * mPerUnit * mPerUnit;
    return (meta.layers || []).map((l, i) => {
      const vol = area * ((l.thickness_mm || 0) / 1000);
      const qty = l.density_t_m3 ? vol * l.density_t_m3 : vol;
      return keep(`lay${i}`, `${l.name || "Layer"} ${l.thickness_mm || 0}mm`, qty, l.density_t_m3 ? "t" : "m³");
    });
  }
  return [];
}

export const fmtQty = (n) =>
  n == null ? "—" : Number(n).toLocaleString("en-NZ", { maximumFractionDigits: 2 });

export const nzd = (n) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n || 0);

export const centroid = (pts) => {
  if (!pts?.length) return { x: 0, y: 0 };
  return {
    x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
    y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
  };
};
